import { useEffect, useState } from "react";
import { getRecommendedJobInfo } from "../../../../utils/api";
import { useLocation } from "react-router";
import { BriefcaseIcon, ExternalLinkIcon, MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/solid";

import { BUTTON_VARIANT_TYPES, NO_ACCESS_TYPES, PERMISSIONS, WORKFORCE_AUSTRALIA_URL } from "../../../../utils/constants";

import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import SectionPlaceholder from "../../../SharedComponents/Loading/SectionPlaceholder";
import Button from "../../../SharedComponents/Button";

function RecommendedJob() {
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [recommendedJobInfo, setRecommendedJobInfo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandCourses, setExpandCourses] = useState([]);

    const location = useLocation();

    const renderWorkStatsName = (data) => {
        let result = data;

        if (data === "aged15To24") {
            result = "Aged 15 to 24";
        } else if (data === "aged25To34") {
            result = "Aged 25 to 34";
        } else if (data === "aged35To44") {
            result = "Aged 35 to 44";
        } else if (data === "aged45Plus") {
            result = "Aged 45 +";
        } else if (data === "fullTime") {
            result = "Full Time";
        } else if (data === "partTime") {
            result = "Part Time";
        } else if (data === "selfEmployed") {
            result = "Self Employed";
        } else if (data === "workforceSize") {
            result = "Workforce Size";
        }

        return result && result.length > 0 ? result[0].toUpperCase() + result.slice(1).toLowerCase() : result;
    };

    useEffect(() => {
        if (location.state.recommendedJobCode) {
            const fetchRecommendedJobInfo = async () => {
                setLoading(true);
                try {
                    const recommendedJobInfoResponse = await getRecommendedJobInfo(location.state.recommendedJobCode);
                    setRecommendedJobInfo(recommendedJobInfoResponse);
                } catch (err) {
                    setAlert(err.message);
                    setAlertType(ALERT_TYPE.ERROR);
                }
                setLoading(false);
            };
            fetchRecommendedJobInfo();
        }
    }, [location.state.recommendedJobCode]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.VIEW_REPORTS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"Recommended Job" + (recommendedJobInfo?.jobTitle ? ": " + recommendedJobInfo?.jobTitle : "")}
                titleIcon={<BriefcaseIcon className="block h-5 w-5 mr-2" />}
            >
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                {loading ? (
                    <SectionPlaceholder />
                ) : (
                    recommendedJobInfo && (
                        <div className="flex flex-col gap-3 text-sm">
                            <div className="flex flex-col gap-3 text-sm">
                                {recommendedJobInfo?.ozjacInformation?.jobDescription && (
                                    <div className="bg-white rounded-xl p-4 shadow-sm">
                                        <div className="flex flex-col gap-2">
                                            <h3 className="text-base font-medium text-indigo-900">Job description</h3>
                                            <div>{recommendedJobInfo.ozjacInformation.jobDescription}</div>
                                        </div>
                                    </div>
                                )}

                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-4">
                                        {recommendedJobInfo?.ozjacInformation?.commonActivities &&
                                            recommendedJobInfo.ozjacInformation.commonActivities.length > 0 &&
                                            <div className="flex flex-col gap-2">
                                                <h3 className="text-base font-medium leading-6 text-indigo-900">Common activities</h3>

                                                <ul className="list-disc ml-8">
                                                    {recommendedJobInfo.ozjacInformation.commonActivities.map((activity, index) => (
                                                        <li key={index}>{activity}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        }

                                        {recommendedJobInfo?.ozjacInformation?.highestInterestCategories && (
                                            <div className="flex flex-col gap-2">
                                                <h3 className="text-sm font-medium text-blue-600">
                                                    Highest Interest Categories
                                                </h3>

                                                <ul className="list-disc ml-8">
                                                    {Object.entries(recommendedJobInfo.ozjacInformation.highestInterestCategories).map(
                                                        ([key, value]) => (
                                                            <li key={key}>{value}</li>
                                                        )
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {recommendedJobInfo?.relatedJobsInAustralia &&
                                (recommendedJobInfo?.relatedJobsInAustralia?.ozJobName || recommendedJobInfo?.relatedJobsInAustralia?.relatedJobs) && (
                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-4">
                                        <h3 className="text-base font-medium leading-6 text-indigo-900">
                                            Related jobs in Australia
                                        </h3>

                                        <div className="bg-blue-gray-50 rounded-xl p-5 shadow-sm">
                                            <div className="flex flex-col gap-4">
                                                {recommendedJobInfo?.relatedJobsInAustralia?.ozJobName && (
                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="text-sm font-medium text-blue-600">
                                                            In Australia, this occupation is known as:
                                                        </h4>

                                                        <ul className="list-disc ml-8">
                                                            <li>
                                                                <span>{recommendedJobInfo.relatedJobsInAustralia.ozJobName}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {recommendedJobInfo?.relatedJobsInAustralia?.relatedJobs && (
                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="font-medium text-blue-600">Related Jobs</h4>

                                                        <ul className="list-disc ml-8">
                                                            {recommendedJobInfo.relatedJobsInAustralia.relatedJobs.map((job, index) => (
                                                                <li key={index}>
                                                                    <span>{job.jobName}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )
                            }

                            {recommendedJobInfo?.workforce && (
                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-4">
                                        <h3 className="text-base font-medium leading-6 text-indigo-900">
                                            Workforce - {recommendedJobInfo?.workforce?.workforceName}
                                        </h3>

                                        {recommendedJobInfo?.workforce?.workforceNote && (
                                            <p>{recommendedJobInfo?.workforce?.workforceNote}</p>
                                        )}

                                        {recommendedJobInfo?.workforce?.workforceDescription &&
                                            recommendedJobInfo?.workforce?.workforceDescription !== "-" && (
                                                <p>
                                                    {recommendedJobInfo?.workforce?.workforceDescription}
                                                </p>
                                            )}

                                        {recommendedJobInfo?.workforce?.workStatistics && (
                                            <div className="bg-slate-50 rounded-xl p-5 shadow-sm">
                                                <div className="flex flex-col gap-4">
                                                    <div className="flex flex-col gap-2">
                                                        <h4 className="font-medium text-blue-600">
                                                            Work Statistics
                                                        </h4>

                                                        <table className="table-auto bg-white rounded-xl">
                                                            <thead>
                                                                <tr className="bg-slate-100 rounded-t-xl">
                                                                    <th className="font-semibold text-left p-2 rounded-tl-xl">
                                                                        Category/Information
                                                                    </th>

                                                                    <th className="font-semibold text-left p-2 rounded-tr-xl">
                                                                        Value
                                                                    </th>
                                                                </tr>
                                                            </thead>

                                                            <tbody className="rounded-b-xl shadow-sm">
                                                                {Object.entries(
                                                                    recommendedJobInfo?.workforce?.workStatistics
                                                                ).map(([key, value], index) => (
                                                                    <tr key={index}>
                                                                        <td className="p-2">
                                                                            {renderWorkStatsName(key)}
                                                                        </td>

                                                                        <td className="p-2">
                                                                            {key !== "workforceSize"
                                                                                ? value.toString() + "%"
                                                                                : value.toString()}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {recommendedJobInfo?.jobProfiles && (
                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-4">
                                        <h3 className="text-base font-medium leading-6 text-indigo-900">Job Profiles</h3>
                                        <p>{recommendedJobInfo?.jobProfiles?.jobProfileDescription && recommendedJobInfo.jobProfiles.jobProfileDescription}</p>

                                        <div className="bg-blue-gray-50 rounded-xl p-5 shadow-sm">
                                            <div className="flex flex-col gap-4">
                                                <div className="flex flex-col gap-1">
                                                    <h4 className="font-medium text-blue-600">Duties</h4>

                                                    {recommendedJobInfo.jobProfiles.duties ?
                                                        (() => {
                                                            if (recommendedJobInfo.jobProfiles.duties.includes("\\par")) {
                                                                const duties = recommendedJobInfo.jobProfiles.duties.split("\\par");

                                                                return duties.map((duty, index) => (
                                                                    <p key={index}>
                                                                        {duty}
                                                                    </p>
                                                                ))
                                                            } else {
                                                                return (
                                                                    <p>
                                                                        {recommendedJobInfo.jobProfiles.duties}
                                                                    </p>
                                                                )
                                                            }
                                                        })() : <p>N/A</p>
                                                    }
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h4 className="font-medium text-blue-600">Specialisations</h4>

                                                    {recommendedJobInfo.jobProfiles.specialis ?
                                                        (() => {
                                                            if (recommendedJobInfo.jobProfiles.specialis.includes("\\par")) {
                                                                const specialis = recommendedJobInfo.jobProfiles.specialis.split("\\par");

                                                                return specialis.map((special, index) => (
                                                                    <p key={index}>
                                                                        {special}
                                                                    </p>
                                                                ))
                                                            } else {
                                                                return (
                                                                    <p>
                                                                        {recommendedJobInfo.jobProfiles.specialis}
                                                                    </p>
                                                                )
                                                            }
                                                        })() : <p>N/A</p>
                                                    }
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h4 className="font-medium text-blue-600">Working Conditions</h4>

                                                    {recommendedJobInfo.jobProfiles.workingConditions ?
                                                        (() => {
                                                            if (recommendedJobInfo.jobProfiles.workingConditions.includes("\\par")) {
                                                                const workingConditions = recommendedJobInfo.jobProfiles.workingConditions.split("\\par");

                                                                return workingConditions.map((workingCondition, index) => (
                                                                    <p key={index}>
                                                                        {workingCondition}
                                                                    </p>
                                                                ))
                                                            } else {
                                                                return (
                                                                    <p>
                                                                        {recommendedJobInfo.jobProfiles.workingConditions}
                                                                    </p>
                                                                )
                                                            }
                                                        })() : <p>N/A</p>
                                                    }
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h4 className="font-medium text-blue-600">Personal Requirements</h4>

                                                    {recommendedJobInfo.jobProfiles.personalRequirement ?
                                                        (() => {
                                                            if (recommendedJobInfo.jobProfiles.personalRequirement.includes("\\par")) {
                                                                const personalRequirement = recommendedJobInfo.jobProfiles.personalRequirement.split("\\par");

                                                                return personalRequirement.map((requirement, index) => (
                                                                    <p key={index}>
                                                                        {requirement}
                                                                    </p>
                                                                ))
                                                            } else {
                                                                return (
                                                                    <p>
                                                                        {recommendedJobInfo.jobProfiles.personalRequirement}
                                                                    </p>
                                                                )
                                                            }
                                                        })() : <p>N/A</p>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {recommendedJobInfo?.jobExplorerAndOutlook && (
                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-4">
                                        <h3 className="text-base font-medium leading-6 text-indigo-900">Job explorer & outlook</h3>
                                        
                                        <p>{recommendedJobInfo?.jobExplorerAndOutlook?.jobExplorerDescription}</p>

                                        <Button
                                            onClick={() =>
                                                (window.open(WORKFORCE_AUSTRALIA_URL, "_blank"))
                                            }
                                            icon={<ExternalLinkIcon className="block h-4 w-4 ml-2" />}
                                            colour={BUTTON_VARIANT_TYPES.GREEN}
                                            text="Job Explorer"
                                        />

                                        <div>
                                            <p>{recommendedJobInfo?.jobExplorerAndOutlook?.jobOutlookDescription}</p>
                                        </div>
                                        
                                        <Button
                                            onClick={() =>
                                                (window.open(WORKFORCE_AUSTRALIA_URL, "_blank"))
                                            }
                                            icon={<ExternalLinkIcon className="block h-4 w-4 ml-2" />}
                                            colour={BUTTON_VARIANT_TYPES.GREEN}
                                            text="Job Outlook"
                                        />
                                    </div>
                                </div>
                            )}

                            {recommendedJobInfo?.courses && (
                                <div className="bg-white rounded-xl p-4 shadow-sm">
                                    <div className="flex flex-col gap-2">
                                        <h3 className="text-base font-medium leading-6 text-indigo-900">Courses</h3>
                                        <ul className="grid lg:grid-cols-2 gap-x-6 gap-y-2">
                                            {recommendedJobInfo?.courses?.coursesNames && recommendedJobInfo?.courses?.coursesNames.map((course, index) => (            
                                                <div key={index}>
                                                    <li>
                                                        <div className="flex flex-row items-center hover:underline cursor-pointer" 
                                                        onClick={() => {
                                                            if (expandCourses.includes(course.courseId)) {
                                                                setExpandCourses(
                                                                    expandCourses.filter(
                                                                        (job) => job !== course.courseId
                                                                    )
                                                                );
                                                            } else {
                                                                setExpandCourses([...expandCourses, course.courseId]);
                                                            }
                                                        }
                                                        }>
                                                            {expandCourses.includes(course.courseId) ? (
                                                                <MinusCircleIcon className="block h-4 w-4 mr-2 text-blue-600  shrink-0" />
                                                                ) : (
                                                                <PlusCircleIcon className="block h-4 w-4 mr-2 text-blue-600 shrink-0"/>
                                                            )}
                                                            
                                                            {course.courseName}
                                                        </div>

                                                        {expandCourses.includes(course.courseId) && 
                                                            <div className="p-3">
                                                                <div className="flex flex-row flex-wrap gap-4 text-xs">
                                                                    {course?.courseSites && course?.courseSites.map((courseSite) => (
                                                                        <div className="bg-blue-gray-50 shadow-md p-3 rounded-xl">
                                                                            <div className="font-medium">{courseSite.providerName}</div>
                                                                            <div>{courseSite.siteName}</div>
                                                                            <div>{courseSite.stateCode}</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                    </li>
                                                </div>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                                )
                            }
                        </div>
                    )
                )
                }
            </DashboardContainer>
        </AccessControl>
    );
}

export default RecommendedJob;
